import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllInventoryReceiptsSingleResponse,
  IGetSingleInventoryReceiptResponse,
  IInventoryReceiptPostRequest,
} from 'types/api/inventoryReceiptsApi.interface'
import { IAddInventoryReceiptState, IInventoryReceiptsSlice } from 'types/inventory.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, getParams, handleErrors } from 'utils/helpers'

const initialState: IInventoryReceiptsSlice = {
  isLoading: false,
  receipts: [],
  singleReceipt: null,
  totalCount: 0,
  totalPages: 0,
}

export const getInventoryReceipts = createAsyncThunk(
  'inventoryReceipts/getInventoryReceipts',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllInventoryReceiptsSingleResponse>> =
        await baseUrl.get('inventory/receipt', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleInventoryReceipt = createAsyncThunk(
  'inventoryReceipts/getSingleInventoryReceipt',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetSingleInventoryReceiptResponse>> =
        await baseUrl.get('inventory/receipt/' + id)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addInventoryReceipt = createAsyncThunk(
  'inventoryReceipts/addInventoryReceipt',
  async ({ data, file }: { data: IAddInventoryReceiptState; file: File }, thunkAPI) => {
    try {
      const request: IInventoryReceiptPostRequest = {
        supplierId: data.supplier?.value,
        receiptDate: data.receiptDate.toISOString(),
        receiptNo: data.receiptNo,
        locationId: data.location?.value,
        currency: data.currency,
        notes: data.notes,
        batches: data.batches.map((b) => ({
          itemId: b.item.value,
          batchCode: b.batchCode,
          quantity: b.quantity,
          unitCost: b.unitCost,
          unitId: b.unit.value,
          mfgDate: b.mfgDate ? b.mfgDate.toISOString() : null,
          expiryDate: b.expiryDate?.toISOString(),
          expiryValue: b.expiryValue,
          startSourceDate: b.startSourceDate?.toISOString(),
          densityKgPerL: b.densityKgPerL,
          serials: b.serials.every((s) => s.serialNo)
            ? b.serials.map((s) => ({
                serialNo: s?.serialNo,
                warrantyExpiry: s?.warrantyExpiry?.toISOString(),
              }))
            : null,
        })),
      }
      const resp: AxiosResponse = await baseUrl.post('inventory/receipt', request)
      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        await baseUrl.post(`inventory/receipt/${resp.data.id}/attachment`, formData)
      }

      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const approveInventoryReceipt = createAsyncThunk(
  'inventoryReceipts/approveInventoryReceipt',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<{ message: string }> = await baseUrl.post(
        'inventory/receipt/' + id + '/approve',
      )
      await thunkAPI.dispatch(getInventoryReceipts(getParams({ location, limit: 30 })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const rejectInventoryReceipt = createAsyncThunk(
  'inventoryReceipts/rejectInventoryReceipt',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<{ message: string }> = await baseUrl.post(
        'inventory/receipt/' + id + '/cancel',
      )
      await thunkAPI.dispatch(getInventoryReceipts(getParams({ location, limit: 30 })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const inventoryReceiptsSlice = createSlice({
  name: 'inventoryReceipts',
  initialState,
  reducers: {
    clearReceipts(state) {
      state.receipts = []
      state.totalPages = 0
      state.totalCount = 0
    },
    clearReceipt(state) {
      state.singleReceipt = null
    },
  },
  extraReducers: {
    [getInventoryReceipts.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryReceipts.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.receipts = payload.data
      state.totalPages = payload.totalPages
      state.totalCount = payload.totalCount
    },
    [getInventoryReceipts.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleInventoryReceipt.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleInventoryReceipt.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.singleReceipt = payload
    },
    [getSingleInventoryReceipt.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addInventoryReceipt.pending.type]: (state) => {
      state.isLoading = true
    },
    [addInventoryReceipt.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Receipt successfully added')
    },
    [addInventoryReceipt.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [approveInventoryReceipt.pending.type]: (state) => {
      state.isLoading = true
    },
    [approveInventoryReceipt.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Receipt successfully approved')
    },
    [approveInventoryReceipt.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [rejectInventoryReceipt.pending.type]: (state) => {
      state.isLoading = true
    },
    [rejectInventoryReceipt.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Receipt successfully rejected')
    },
    [rejectInventoryReceipt.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearReceipts, clearReceipt } = inventoryReceiptsSlice.actions

export default inventoryReceiptsSlice.reducer
